import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { navPrimary } from "@/data/site";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [location.pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled || open
          ? "bg-black/70 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" data-testid="nav-logo" className="flex items-center group">
            <span className="h-9 w-9 grid place-items-center bg-burnt text-white font-heading font-bold text-lg transition-colors group-hover:bg-burnt/90">
              EG
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navPrimary.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.to === "/" ? "home" : l.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `px-3.5 py-2 text-sm font-medium transition-colors ${
                    isActive ? "text-white" : "text-zinc-300 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <Link
            to="/contact"
            data-testid="nav-cta-contact"
            className="hidden lg:inline-flex items-center whitespace-nowrap bg-burnt hover:bg-burnt-hover text-white text-sm font-medium px-5 py-2.5 transition-colors"
          >
            Work With Me
          </Link>

          <button
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden text-white p-2"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10 max-h-[80vh] overflow-y-auto"
        >
          <nav className="px-4 py-3">
            {navPrimary.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-link-${l.to === "/" ? "home" : l.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `block py-3 text-base border-b border-white/5 ${
                    isActive ? "text-burnt" : "text-zinc-200"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              className="mt-4 mb-2 inline-flex w-full justify-center items-center bg-burnt text-white text-sm font-medium px-5 py-3"
            >
              Work With Me
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
